import React, { useState } from 'react';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  computeOpenSlotDropValidity,
  heldDaySectionsFor,
  planDragDrop,
  runDragDrop,
} from '@load-balancing/services';
import type { ShiftOverrideSnapshotType } from '@load-balancing/services';
import { ASSIGN_SHIFT_MUTATION } from '@load-balancing/graphql/mutations/assign-shift';
import { UNASSIGN_SHIFT_MUTATION } from '@load-balancing/graphql/mutations/unassign-shift';
import type {
  ScheduleDayType,
  ScheduleDragStateType,
  ScheduleDropValidityType,
  ScheduleShiftOverrideType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import {
  dragDropToast,
  parseOpenShiftId,
  REFETCH_AFTER_ASSIGN,
  REFETCH_AFTER_UNASSIGN,
} from './_schedule-review-page-service';

type UseScheduleReviewPageDragDropParamsType = {
  runId: string | undefined;
  organizationUnitId: string | undefined;
  members: ScheduleTeamMemberType[];
  // The grid shifts with the page's in-session overrides already layered in.
  effectiveShifts: ScheduleShiftType[];
  // The active week's day columns — maps a shift's dayKey to its calendar date so an open slot's
  // charge-eligibility is read from the WorkerTerm covering that date (WORK-6528). Same scope as
  // effectiveShifts (one Sun–Sat week), so dayKey is unambiguous within it.
  days: ScheduleDayType[];
  // The run service line's cross-role EligibilityRule matrix as "workerRole->slotRole" abbrev pairs
  // (WORK-6513), forwarded to computeOpenSlotDropValidity so the drop ring honors it.
  eligibleRolePairs: ReadonlySet<string>;
  // The page's in-session override map + setter — shared with the panel/suggestion edits, so the
  // optimistic drag overlay is built here from the same source of truth rather than duplicated.
  shiftOverrides: Record<string, ScheduleShiftOverrideType>;
  setShiftOverrides: React.Dispatch<
    React.SetStateAction<Record<string, ScheduleShiftOverrideType>>
  >;
  // Re-point the open Shift Detail panel onto the slot a drop just filled, so it doesn't close
  // when the open-row id it was keyed on disappears on refetch. Called with the drop target id and
  // the newly-filled shift's uuid; the page re-points only when the panel was on that target.
  onAssigned?: (toShiftId: string, affectedShiftId: string) => void;
};

export type UseScheduleReviewPageDragDropReturnType = {
  // Locks the grid for the whole drag-mutation sequence (incl. a move's two legs).
  isDragMutating: boolean;
  handleWorkerDragStart: (state: ScheduleDragStateType) => void;
  handleWorkerDragEnd: () => void;
  handleGridDragStart: (shiftId: string) => void;
  getDropValidity: (shiftId: string) => ScheduleDropValidityType;
  handleDropAssign: (toShiftId: string) => void;
};

/**
 * Owns drag-to-assign for the schedule review grid (WORK-6399): the dedicated assign/unassign
 * mutations, the drag state, the optimistic override overlay, and the drop orchestration. Extracted
 * from the page so the grid wiring stays a handful of props (the page file was approaching 1k lines).
 *
 * The mutations are toast-SILENT (no `onCompleted`/`onError`) and pin `errorPolicy: 'none'` on
 * purpose. The app's global default is `errorPolicy: 'all'` (legacy/lib/graph-ql.js), under which a
 * FAILED mutation RESOLVES with `{ errors }` instead of rejecting — which would make a rejected drop
 * look like a success (clear the overlay, no revert). Pinning `'none'` with no `onError` makes the
 * mutate fn REJECT, which is what `runDragDrop` needs to sequence a move's two legs and roll back the
 * optimistic overlay. The single user-facing toast is raised HERE from `runDragDrop`'s returned
 * outcome (`dragDropToast`): a move surfaces one "Worker moved.", not the two its legs would fire.
 */
export function useScheduleReviewPageDragDrop({
  runId,
  organizationUnitId,
  members,
  effectiveShifts,
  days,
  eligibleRolePairs,
  shiftOverrides,
  setShiftOverrides,
  onAssigned,
}: UseScheduleReviewPageDragDropParamsType): UseScheduleReviewPageDragDropReturnType {
  const [assignShiftDrag] = useMutation(ASSIGN_SHIFT_MUTATION, {
    refetchQueries: REFETCH_AFTER_ASSIGN,
    awaitRefetchQueries: true,
    errorPolicy: 'none',
  });
  const [unassignShiftDrag] = useMutation(UNASSIGN_SHIFT_MUTATION, {
    refetchQueries: REFETCH_AFTER_UNASSIGN,
    awaitRefetchQueries: true,
    errorPolicy: 'none',
  });

  // The worker currently being dragged (from a candidate row, team row, or grid cell), or null.
  const [dragState, setDragState] = useState<ScheduleDragStateType>(null);
  const [isDragMutating, setIsDragMutating] = useState(false);

  const draggedWorker = dragState
    ? (members.find((member) => member.id === dragState.workerId) ?? null)
    : null;

  // ── Optimistic override overlay ────────────────────────────────────────────────
  // Built from the page's shared override state. Reading from the render closure is race-free: the
  // grid is locked (`isDragMutating`) for the whole sequence, so no other write touches
  // `shiftOverrides` between snapshot and reconcile.
  const patchOverride = (shiftId: string, patch: ScheduleShiftOverrideType) =>
    setShiftOverrides((prev) => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], ...patch },
    }));
  // Capture the involved override keys' prior values before an optimistic drag patch.
  const snapshotOverrides = (shiftIds: string[]): ShiftOverrideSnapshotType =>
    Object.fromEntries(shiftIds.map((id) => [id, shiftOverrides[id]]));
  // Delete override keys once a drag mutation succeeds: the refetched server truth now drives those
  // cells, and a lingering override keyed to a reused synthetic open id would double-render a worker.
  const clearOverrides = (shiftIds: string[]) =>
    setShiftOverrides((prev) => {
      const next = { ...prev };
      shiftIds.forEach((id) => delete next[id]);
      return next;
    });
  // Roll an optimistic drag patch back to its snapshot — re-set the captured prior values and delete
  // the keys that had no override before the drag.
  const restoreOverrides = (snapshot: ShiftOverrideSnapshotType) =>
    setShiftOverrides((prev) => {
      const next = { ...prev };
      Object.entries(snapshot).forEach(([id, value]) => {
        if (value === undefined) delete next[id];
        else next[id] = value;
      });
      return next;
    });

  // Candidate-row / team-row sources carry their own worker; the page just records the drag.
  const handleWorkerDragStart = (state: ScheduleDragStateType) =>
    setDragState(state);
  const handleWorkerDragEnd = () => setDragState(null);

  // Grid source: resolve the worker assigned to the dragged shift by uuid (`assignedWorkerId`),
  // falling back to name only when it is absent — names collide, ids do not.
  const handleGridDragStart = (shiftId: string) => {
    const shift = effectiveShifts.find((entry) => entry.id === shiftId);
    if (!shift) return;
    const member =
      (shift.assignedWorkerId
        ? members.find((entry) => entry.id === shift.assignedWorkerId)
        : undefined) ??
      (shift.workerName
        ? members.find((entry) => entry.name === shift.workerName)
        : undefined);
    if (!member) return;
    setDragState({
      workerId: member.id,
      workerName: member.name,
      source: 'grid',
      fromShiftId: shiftId,
    });
  };

  // Day+band slots the dragged worker already holds — the client proxy for a time conflict (open
  // cells carry no start/end time). `effectiveShifts` is the single active week's shifts, which is
  // what keeps the weekKey-less proxy key unambiguous (see heldDaySectionsFor). Recomputed only when
  // the shifts, the dragged worker, or the drag's source shift change.
  const heldDaySections = React.useMemo(
    () =>
      heldDaySectionsFor(
        effectiveShifts,
        draggedWorker,
        dragState?.fromShiftId
      ),
    [effectiveShifts, draggedWorker, dragState?.fromShiftId]
  );

  // dayKey → calendar date for the active week, so an open slot resolves to the date that drives its
  // per-slot charge-eligibility term lookup (WORK-6528).
  const dateByDayKey = React.useMemo(
    () => new Map(days.map((day) => [day.key, day.date])),
    [days]
  );

  const getDropValidity = (shiftId: string): ScheduleDropValidityType => {
    const shift = effectiveShifts.find((entry) => entry.id === shiftId);
    if (!shift) return { valid: false };
    return computeOpenSlotDropValidity(
      draggedWorker,
      shift,
      heldDaySections,
      dateByDayKey.get(shift.dayKey),
      eligibleRolePairs
    );
  };

  // Drop onto an open slot: resolve the plan (noop | assign | move), then run it with an optimistic
  // overlay that reconciles against the server refetch and raise the single outcome toast.
  // `isDragMutating` locks the grid for the whole sequence — including the gap between a move's two
  // legs, where both hook loading flags momentarily read false — and clears in `finally` whether the
  // sequence succeeds, reverts, or partially fails.
  const handleDropAssign = (toShiftId: string) => {
    const plan = planDragDrop(dragState, toShiftId, {
      runId,
      organizationUnitId,
      parseOpenShiftId,
    });
    setDragState(null);
    if (plan.kind === 'noop') return;

    setIsDragMutating(true);
    runDragDrop(plan, {
      assign: (variables) => assignShiftDrag({ variables }),
      unassign: (variables) => unassignShiftDrag({ variables }),
      snapshotOverrides,
      patchOverride,
      restoreOverrides,
      clearOverrides,
    })
      .then((outcome) => {
        const toast = dragDropToast(outcome);
        if (toast) createMessage(toast.kind, toast.message);
        // Keep an open panel on the slot it just filled (the open-row id it was keyed on is
        // gone after refetch) — mirrors the button-assign path's re-point.
        if (
          (outcome.kind === 'assigned' || outcome.kind === 'moved') &&
          outcome.affectedShiftId
        ) {
          onAssigned?.(toShiftId, outcome.affectedShiftId);
        }
      })
      .finally(() => setIsDragMutating(false));
  };

  return {
    isDragMutating,
    handleWorkerDragStart,
    handleWorkerDragEnd,
    handleGridDragStart,
    getDropValidity,
    handleDropAssign,
  };
}
